import { ctx } from './ctx.js';
import { wf } from './state.js';
import { apiGet } from './api.js';
import { TBL_REV, BIM_MS_TYPE_IDS, INTERIOR_CONCEPT_TYPE_ID } from './constants.js';
import { fmt, daysFrom, getFirst, msKey, phKey, sectionKey, isNodeCollapsed } from './utils.js';
import { buildSectionNode } from './builders.js';
import { buildConceptRow, loadConcepts } from './design.js';
import { buildBimRow, buildSheetRow, buildSheetDisciplineGroupsHtml } from './production.js';

/* ── State lookups ──────────────────────────────────────────────────────── */
export function getMsById(msId) {
  var ms = ctx.state.milestones;
  for (var i = 0; i < ms.length; i++) { if (ms[i].id === msId) return ms[i]; }
  return null;
}

export function getPhaseById(phId) {
  var ph = ctx.state.phases;
  for (var i = 0; i < ph.length; i++) { if (ph[i].id === phId) return ph[i]; }
  return null;
}

export function getMsPhase(ms) {
  var pname = ms.fields['Project Phase'] || '';
  var ph = ctx.state.phases;
  for (var i = 0; i < ph.length; i++) { if ((ph[i].fields['Phase Name'] || '') === pname) return ph[i]; }
  return null;
}

export function getMilestonesForPhase(phId) {
  var ph = getPhaseById(phId);
  if (!ph) return [];
  var pname = ph.fields['Phase Name'] || '';
  var ms = ctx.state.milestones.filter(function(m) { return (m.fields['Project Phase'] || '') === pname; });
  if (!wf.searchQuery) return ms;
  var q = wf.searchQuery.toLowerCase();
  return ms.filter(function(m) {
    var name = (m.fields['Full Deliverable Name'] || m.fields['Name'] || '').toLowerCase();
    if (name.indexOf(q) !== -1) return true;
    var items = ctx.clCache[m.id] || [];
    for (var i = 0; i < items.length; i++) {
      var iname = (getFirst(items[i].fields['Name for List (from Deliverable Checklist Item Template)']) || '').toLowerCase();
      if (iname.indexOf(q) !== -1) return true;
    }
    var sheets = ctx.sheetCache[m.id] || [];
    for (var s = 0; s < sheets.length; s++) {
      var _sv2 = sheets[s].fields['Sheet Number and Title (from Drawing Sheet)'] || sheets[s].fields['Sheet Number and Title'] || '';
      var sname = (Array.isArray(_sv2) ? (_sv2[0] || '') : String(_sv2)).toLowerCase();
      if (sname.indexOf(q) !== -1) return true;
    }
    return false;
  });
}

/* ── Schedule root ──────────────────────────────────────────────────────── */
export function buildScheduleChildren() {
  var h = '';
  ctx.state.phases.forEach(function(ph) {
    if (wf.searchQuery && getMilestonesForPhase(ph.id).length === 0) return;
    h += buildPhaseNode(ph);
  });
  return h;
}

/* ── Milestone sheet loader ─────────────────────────────────────────────── */
export function loadSheets(msId) {
  if (ctx.sheetCache[msId] !== undefined) return;
  ctx.sheetCache[msId] = null;
  var formula = 'FIND("' + msId + '",ARRAYJOIN({Record ID (from Deliverables)},","))';
  /* No fields[] restriction — lookup fields return empty when filtered. Fetch all, sort by order.
     Uses "Drawing Sort Order from Drawing Sheet", not "Sheet Sort Order (from Drawing Sheet)" —
     the latter's formula has no branch for the "Special General" discipline (FDSAP) and falls
     through to a bare "-", which sorts before every real value (2026-07-23). */
  var qs = 'filterByFormula=' + encodeURIComponent(formula)
    + '&sort%5B0%5D%5Bfield%5D=Drawing+Sort+Order+from+Drawing+Sheet&sort%5B0%5D%5Bdirection%5D=asc&maxRecords=100';
  apiGet(TBL_REV, qs, function(d) {
    var seen = {}, sheets = [];
    var records = d ? d.records : [];
    for (var i = 0; i < records.length; i++) {
      var sheetId = getFirst(records[i].fields['Record ID (from Drawing Sheet)']);
      if (sheetId && !seen[sheetId]) {
        seen[sheetId] = true;
        sheets.push(records[i]);
      }
    }
    sheets.sort(function(a, b) {
      var sortVal = function(s) {
        var v = s.fields['Drawing Sort Order from Drawing Sheet'];
        var n = parseFloat(Array.isArray(v) ? v[0] : v);
        return isNaN(n) ? 999999 : n;
      };
      var numTitle = function(s) {
        var v = s.fields['Sheet Number and Title (from Drawing Sheet)'];
        return (Array.isArray(v) ? v[0] : v) || '';
      };
      return sortVal(a) - sortVal(b) || numTitle(a).localeCompare(numTitle(b));
    });
    ctx.sheetCache[msId] = sheets;
    if (wf.collapsed[msKey(msId)] === false) {
      var _msId = msId;
      var _si = ctx.sheetCache[_msId] || [];
      if (_si.length > 0) {
        ctx.refreshSection(_msId, 'sheets', function() {
          return buildSectionNode(_msId, 'sheets', 'Drawing Sheets', buildSheetDisciplineGroupsHtml(_msId, _si), _si.length);
        });
      } else {
        var _sEl = document.querySelector('.wf-node[data-key="' + sectionKey(_msId, 'sheets') + '"]');
        if (_sEl) {
          var _rcEl2 = document.getElementById('aj-sc-sections');
          if (_rcEl2 && _rcEl2.contains(_sEl)) {
            /* React owns this node — notify instead of removing */
            window.dispatchEvent(new Event('outline-render'));
          } else {
            _sEl.parentNode.removeChild(_sEl);
          }
        }
      }
    }
  });
}

/* ── Zoom views ─────────────────────────────────────────────────────────── */
export function buildPhaseZoom(phId) {
  /* React ScheduleSection handles phase zoom view */
  return '<div class="wf-list"></div>';
}

export function buildMsZoom(msId) {
  /* MilestoneSections (React) renders all section content for the zoomed milestone.
     The zoom title already shows the milestone name. Just ensure the ms is "expanded". */
  wf.collapsed['ms:' + msId] = false;
  return '<div class="wf-list"></div>';
}

/* ── Node builders ──────────────────────────────────────────────────────── */
function shouldHideDoneForMs(msId, phId) {
  var keys = wf.hideDoneKeys;
  if (!keys) return false;
  return !!(keys['global'] || (phId && keys['ph:' + phId]) || keys['ms:' + msId]);
}

export function buildPhaseNode(ph) {
  var phId = ph.id;
  var key = phKey(phId);
  var isCollapsed = isNodeCollapsed(key);
  var name = ph.fields['Phase Name'] || ph.fields['Name'] || 'Phase';
  var allowTime = ph.fields['Allow Time Entries'] === true;
  var contracted = parseFloat(ph.fields['Contracted Fee'] || 0);
  var expended = parseFloat(ph.fields['Fee Expended'] || 0);
  var pct = contracted > 0 ? Math.min(Math.round(expended / contracted * 100), 100) : 0;
  var remaining = parseFloat(ph.fields['Fee Remaining'] || 0);
  var barColor = pct > 90 ? '#c0392b' : pct > 75 ? '#b8540a' : '#1D9E75';
  var milestones = getMilestonesForPhase(phId);
  var activeMsCount = milestones.filter(function(m) { return !m.fields['Posted Date']; }).length;
  var timeOpenNoWork = allowTime && activeMsCount === 0;

  var h = '<div class="wf-node wf-node--phase' + (isCollapsed ? ' wf-node--collapsed' : '') + '" data-key="' + key + '">';
  h += '<div class="wf-row" data-key="' + key + '">';
  h += '<button class="wf-triangle" data-fn="toggleCollapse" data-key="' + key + '" title="' + (isCollapsed ? 'Expand' : 'Collapse') + '">';
  h += '<span class="wf-tri-icon ' + (isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down') + '"></span>';
  h += '</button>';
  h += '<button class="wf-bullet wf-bullet--phase" data-fn="zoomTo" data-level="phase" data-pid="' + phId + '" title="Focus on this phase"></button>';
  h += '<div class="wf-text-wrap">';
  h += '<span class="wf-node-text wf-node-text--phase">' + name + '</span>';
  h += '<span class="wf-meta">';
  if (contracted > 0) {
    var truePct = Math.round(expended / contracted * 100);
    var overBudget = truePct > 100;
    var displayPct = Math.min(truePct, 100);
    var feeLabel = truePct + '%';
    var feeColor = overBudget ? '#c0392b' : truePct > 90 ? '#b8540a' : truePct > 75 ? '#b8540a' : '#1D9E75';
    var overAmt = overBudget ? Math.round((expended - contracted) / 1000) : 0;
    h += '<span class="wf-fee-bar-wrap"><span class="wf-fee-bar-fill" style="width:' + displayPct + '%;background:' + feeColor + ';"></span></span>';
    h += '<span class="wf-fee-pct" style="color:' + feeColor + ';">' + feeLabel + ' fee</span>';
    if (overBudget) {
      h += '<span class="wf-fee-pct" style="color:#c0392b;">$' + overAmt + 'k over</span>';
    } else if (remaining > 0) {
      h += '<span class="wf-fee-rem">$' + Math.round(remaining / 1000) + 'k left</span>';
    }
    var ebr = parseFloat(ph.fields['Effective Billing Rate'] || 0);
    if (ebr > 0) h += '<span class="wf-fee-rem">EBR $' + Math.round(ebr) + '</span>';
  }
  h += '<label class="wf-inline-toggle" title="' + (allowTime ? 'Disable time entries' : 'Enable time entries') + '">';
  h += '<input type="checkbox" class="wf-inline-toggle-inp" data-pid="' + phId + '" data-val="' + allowTime + '" ' + (allowTime ? 'checked' : '') + '>';
  h += '<span class="wf-inline-toggle-track"><span class="wf-inline-toggle-thumb"></span></span>';
  h += '</label>';
  if (timeOpenNoWork) h += '<span class="wf-phase-signal wf-phase-signal--amber">Time open, no active work</span>';
  h += '</span>';
  h += '</div>';
  h += '<button class="wf-menu-btn" data-fn="openMenu" data-key="' + key + '" data-type="phase" data-pid="' + phId + '" data-allowtime="' + allowTime + '">&#8943;</button>';
  h += '</div>';
  if (wf.menuNodeId === key) h += ctx.buildMenu('phase', { phId: phId, allowTime: allowTime });
  if (!isCollapsed) {
    h += '<div class="wf-children">';
    milestones.forEach(function(ms) { h += buildMsNode(ms, phId); });
    h += '</div>';
  }
  h += '</div>';
  return h;
}

export function buildMsNode(ms, phId, inZoom) {
  var msId = ms.id;
  var key = msKey(msId);
  var isCollapsed = isNodeCollapsed(key, false);
  var f = ms.fields;
  var isPosted = !!(f['Posted Date']);
  var name = f['Full Deliverable Name'] || f['Name'] || 'Untitled';
  var lft = f['Latest Finish (LFT)'] || '';
  var da = daysFrom(lft);
  var urgency = f['Urgency Message'] || '';
  var rawFloat = parseFloat(f['Slack'] || 0);
  var completed = parseInt(f['Completed Checklist Items'] || 0);
  var total = completed + parseInt(f['Incomplete Checklist Items'] || 0);
  var resp = f['Responsible (Text)'] || '';
  var isDirty = !!wf.dirty[msId];
  var isEditingLft = wf.editingLft === msId;
  var urgColor = '#7a9aaa';
  var urgTxt = '';
  if (urgency.indexOf('IMPOSSIBLE') !== -1) { urgColor = '#c0392b'; urgTxt = 'Impossible'; }
  else if (da !== null && da < 0) { urgColor = '#c0392b'; urgTxt = Math.abs(da) + 'd overdue'; }
  else if (urgency.indexOf('URGENT') !== -1 || (da !== null && da <= 2)) { urgColor = '#b8540a'; urgTxt = 'Urgent'; }
  else if (urgency.indexOf('HEATING') !== -1) { urgColor = '#b8540a'; urgTxt = 'Soon'; }
  var msHiddenClass = isPosted && shouldHideDoneForMs(msId, phId) ? ' wf-hidden-done' : '';
  var h = '<div class="wf-node wf-node--ms' + (isDirty ? ' wf-node--dirty' : '') + (isPosted ? ' wf-node--posted' : '') + (!isPosted && isCollapsed ? ' wf-node--collapsed' : '') + msHiddenClass + '" data-key="' + key + '">';
  h += '<div class="wf-row" data-key="' + key + '">';
  h += '<button class="wf-triangle wf-triangle--ms" data-fn="toggleCollapse" data-key="' + key + '">';
  h += '<span class="wf-tri-icon ' + (isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down') + '"></span>';
  h += '</button>';
  h += '<button class="wf-bullet wf-bullet--ms" data-fn="zoomTo" data-level="ms" data-msid="' + msId + '" data-pid="' + (phId || '') + '" title="Focus on this milestone"></button>';
  h += '<div class="wf-text-wrap">';
  h += '<span class="wf-node-text wf-node-text--ms">' + name + (resp ? '<span class="wf-resp-inline">R: ' + resp + '</span>' : '') + '</span>';
  h += '<span class="wf-meta">';
  if (isPosted) {
    h += '<span class="wf-date-btn" style="color:#1D9E75;">Posted ' + fmt(f['Posted Date']) + '</span>';
    h += '</span>';
    h += '</div>';
  } else if (isEditingLft) {
    h += '<input class="wf-lft-input" id="wf-lft-' + msId + '" type="date" value="' + lft + '" data-msid="' + msId + '">';
  } else {
    var isPinned = f['Date Pinned'] === true;
    h += '<button class="wf-date-btn' + (da !== null && da < 0 ? ' wf-date-btn--red' : da !== null && da < 3 ? ' wf-date-btn--amber' : '') + '" data-fn="editLft" data-msid="' + msId + '">';
    if (isPinned) h += '<svg width="10" height="10" viewBox="0 0 16 16" fill="currentColor" style="margin-right:3px;opacity:0.6;"><path d="M9.5 1.5l5 5-2 1-3-3-4 4 1 1-3 5-2-2 5-3 1 1 4-4-3-3 1-2z"/></svg>';
    h += (lft ? fmt(lft) : '--') + '</button>';
  }
  if (!isPosted) {
    h += '<span class="wf-float" style="color:' + (rawFloat < 0 ? '#c0392b' : rawFloat < 3 ? '#b8540a' : '#7a9aaa') + ';">' + (rawFloat < 0 ? '0d' : Math.round(rawFloat) + 'd') + '</span>';
    if (total > 0) h += '<span class="wf-prog' + (completed === total ? ' wf-prog--done' : '') + '">' + completed + '/' + total + '</span>';
    if (urgTxt) h += '<span class="wf-urg-tag" style="color:' + urgColor + ';">' + urgTxt + '</span>';
    if (!resp) h += '<span class="wf-flag wf-flag--red">No responsible</span>';
    if (isDirty) h += '<span class="wf-dirty-dot"></span>';
    h += '</span>';
    h += '</div>';
  }
  h += '<button class="wf-menu-btn" data-fn="openMenu" data-key="' + key + '" data-type="ms" data-msid="' + msId + '" data-pid="' + (phId || '') + '">&#8943;</button>';
  h += '</div>';
  if (wf.menuNodeId === key) h += ctx.buildMenu('ms', { msId: msId, phId: phId, msPosted: ms.fields['Posted Date'] || ms.fields['Posted'] || '' });
  if (wf.panels[key]) h += wf.panels[key];
  if (!isCollapsed && !inZoom) {
    h += '<div class="wf-children">';
    var clItems = ctx.clCache[msId];
    var clInner = '';
    if (!clItems) {
      clInner = '<div class="wf-loading">Loading...</div>';
    } else {
      var cats = ctx.getCategoryNodes(msId, clItems);
      cats.forEach(function(cat) { clInner += ctx.buildCatNode(cat, msId); });
    }
    h += buildSectionNode(msId, 'checklist', 'Checklist', clInner, clItems ? clItems.length : null);
    var _msDelTypeId = (ms.fields['Deliverable'] || [])[0] || '';
    if (_msDelTypeId === INTERIOR_CONCEPT_TYPE_ID) {
      var conceptsInner = '';
      if (ctx.conceptCache === null) {
        conceptsInner = '<div class="wf-loading">Loading concepts...</div>';
        loadConcepts();
      } else if (ctx.conceptCache.length === 0) {
        conceptsInner = '<div class="wf-loading" style="color:#7a9aaa;">No concepts</div>';
      } else {
        for (var cci = 0; cci < ctx.conceptCache.length; cci++) {
          conceptsInner += buildConceptRow(ctx.conceptCache[cci], msId);
        }
      }
      h += buildSectionNode(msId, 'concepts', 'Concepts', conceptsInner, ctx.conceptCache ? ctx.conceptCache.length : null);
    }
    var _msBimTypeId = (ms.fields['Deliverable'] || [])[0] || '';
    var _isBimMs = BIM_MS_TYPE_IDS.indexOf(_msBimTypeId) !== -1;
    var bimItems = _isBimMs ? ctx.bimCache : null;
    if (bimItems && bimItems.length > 0) {
      var bimInner = '';
      for (var bi = 0; bi < bimItems.length; bi++) bimInner += buildBimRow(bimItems[bi]);
      h += buildSectionNode(msId, 'bim', 'BIM Systems', bimInner, bimItems.length);
    }
    var sheetItems = ctx.sheetCache[msId];
    if (sheetItems && sheetItems.length > 0) {
      h += buildSectionNode(msId, 'sheets', 'Drawing Sheets', buildSheetDisciplineGroupsHtml(msId, sheetItems), sheetItems.length);
    }
    h += '</div>';
  }
  h += '</div>';
  return h;
}
